<template>
  <b-col class="m-auto">
    <slot name="logo"></slot>

    <h4 v-if="onlySSO" role="heading" class="text-muted text-center only-sso-message" id="login-title-message">
      {{ labels.titleSSO }}
    </h4>

    <b-row v-if="showSSOButton('google')" class="mt-3">
      <b-col cols="12">
        <b-button tabindex="1" type="submit" variant="light" :disabled="submitInProgress" class="col-12"
          @click="googleLogin()">
          <em class="pr-2 mdi mdi-google text-red"></em>
          {{ labels.googleLogin }}
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="showSSOButton('microsoft')" class="mt-3">
      <b-col cols="12">
        <b-button tabindex="2" type="submit" variant="light" :disabled="submitInProgress" class="col-12"
          @click="microsoftLogin()">
          <em class="pr-2 mdi mdi-microsoft text-blue"></em>
          {{ labels.microsoftLogin }}
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="isCustomProvider" class="mt-3">
      <b-col cols="12">
        <b-button tabindex="2" type="submit" variant="light" :disabled="submitInProgress" class="col-12"
          @click="customProviderLogin()">
          <em class="pr-2 mdi mdi-shield-account"></em>
          {{ customProviderButtonLabel }}
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="onlySSO && message" align-h="center" class="mt-3">
      <span role="alert" class="text-secondary mt-1 error-message">
        {{ message }}
      </span>
    </b-row>

    <div v-if="!onlySSO" class="line-with-or">{{ labels.or }}</div>

    <b-form v-if="!onlySSO" @submit.prevent="login" novalidate ref="form">
      <CustomInput type="text" v-model="form.email" tabindex="1" :labelUp="true" :label="labels.emailCode"
        :labelWrong="labels.emailCodeWrong" :aria-label="labels.emailCodeHelp" />

      <b-tooltip target="text">
        <div class="tooltip-info">
          {{ labels.emailCodeHelp }}
        </div>
      </b-tooltip>

      <CustomInput type="password" v-model="form.password" :labelUp="true" :label="labels.password"
        :labelWrong="labels.password" :disablePasswordVerifications="true" />

      <b-row v-if="message" align-h="center">
        <span role="alert" class="text-secondary mt-1 error-message">
          {{ message }}
        </span>
      </b-row>

      <Recaptcha ref="recaptcha" @verify="recaptchaVerify" @error="recaptchaError">
        <b-button tabindex="0" type="submit" variant="quartenary" :disabled="submitInProgress"
          class="col-12 mt-3 text-white">
          <em class="mdi mdi-spin pr-2" :class="{ 'mdi-loading': submitInProgress }"></em>
          {{ labels.login }}
        </b-button>
      </Recaptcha>
    </b-form>

    <b-row v-if="!onlySSO" align-h="center" class="mt-3 text-center">
      <b-col class="forgot-and-register">
        <b-link class="text-center" id="link-reset" :to="{ name: 'login.password', params: { email: form.email } }">
          {{ labels.passwordReset }}
        </b-link>
      </b-col>
    </b-row>

    <ModalLinkAccount ref="link-account-modal" :linkAccount="linkAccount" @doLink="doLinkAccounts" />
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import ability from '@/helpers/ability'
import unconfirmedValidation from '@/helpers/unconfirmedValidation'
import Recaptcha from '@/components/Shared/Recaptcha.vue'
import ModalLinkAccount from '@/components/Modal/ModalLinkAccount.vue'
import {
  getUser,
  saveUserData,
  isEnterprisePlan,
  isToNewLayout,
  isEarlyAccessTrialAccount,
  generateCookie,
} from '@/service/user'
import { login, loginWithToken } from '@/service/account'
import {
  projectAuth,
  OAuthProvider,
  SAMLAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  setPersistence,
  inMemoryPersistence,
} from '@/firebase/config'
import { AuthErrorCodes } from '@/helpers/authErrorCodes'
import {
  CUSTOM_PROVIDER_MAP,
  PROVIDER_DISPLAY_NAME,
  isValidProvider,
} from '@/helpers/ssoProviders'

export default {
  components: {
    CustomInput,
    Recaptcha,
    ModalLinkAccount,
  },

  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'login' })

    const { code, provider = null } = this.$route.query
    const validProvider = provider && isValidProvider(provider) ? provider : null

    this.onlySSO =
      code === AuthErrorCodes.SSORedirect ||
      !!validProvider

    this.provider = validProvider
  },

  data() {
    return {
      linkAccount: null,
      tenantId: process.env.VUE_APP_FB_TENANTID || undefined,
      submitInProgress: false,
      message: null,
      form: {
        email: undefined,
        password: undefined,
        googleRecaptcha: undefined,
      },
      onlySSO: false,
      provider: null,
    }
  },

  computed: {
    ...mapGetters(['resources']),
    labels() {
      return {
        ...this.resources.page.login.auth,
        ...this.resources.general.input,
        ...this.resources.general.buttons,
        ...this.resources.general.errors,
      }
    },

    canAccessNewProfile() {
      return (
        (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
        !this.$can('access', 'read-only')
      )
    },

    isCustomProvider() {
      return this.provider && !['google', 'microsoft'].includes(this.provider)
    },

    /**
     * Resolve o provider ID do Firebase a partir do query param.
     * Usa o dicionário CUSTOM_PROVIDER_MAP para retrocompatibilidade com clientes legados,
     * ou usa o valor diretamente se já vier com prefixo (oidc.* / saml.*).
     */
    resolvedProviderId() {
      const { provider } = this
      if (!provider) return null
      return CUSTOM_PROVIDER_MAP[provider] || provider
    },

    customProviderButtonLabel() {
      const { provider } = this
      if (!provider) return ''
      const displayName =
        PROVIDER_DISPLAY_NAME[provider] ||
        provider.replace(/^(oidc|saml)\./, '')
      return (this.labels.ssoLogin || '{provider}').replace('{provider}', displayName)
    },
  },

  methods: {
    showSSOButton(providerReference) {
      const { onlySSO, provider } = this

      return (
        !onlySSO ||
        (onlySSO && !provider) ||
        (onlySSO && provider === providerReference)
      )
    },

    recaptchaVerify(value) {
      this.form.googleRecaptcha = value
      this._doLogin()
    },

    recaptchaError() {
      this.submitInProgress = false
      this.$refs.recaptcha.resetRecaptcha()
    },

    getProviderForProviderId(providerId) {
      if ('microsoft.com' === providerId) {
        const provider = new OAuthProvider(providerId)
        provider.addScope('User.Read')
        return provider
      } else if ('google.com' === providerId) {
        return new GoogleAuthProvider()
      } else if (providerId && providerId.startsWith('saml.')) {
        return new SAMLAuthProvider(providerId)
      } else if (providerId) {
        // OIDC providers — aceita tanto 'oidc.xxx' quanto fallback para OAuthProvider
        return new OAuthProvider(providerId)
      }
      return null
    },

    /**
     * Descobre dinamicamente qual provider já está vinculado ao email
     * e exibe o modal de link de contas.
     * Usa fetchSignInMethodsForEmail para não depender de mapeamento hardcoded.
     */
    async handleLinkAccountError(error, fromProviderId) {
      const email = error.customData.email
      try {
        const methods = await fetchSignInMethodsForEmail(projectAuth, email)
        // Filtrar 'password' — linking só funciona com providers OAuth/SAML
        // pois usa signInWithPopup para autenticar antes de vincular
        const oauthMethods = methods.filter(m => m !== 'password')
        if (!oauthMethods.length) {
          // Conta usa apenas email/senha — não é possível linkar via popup
          this.message = this.labels.unexpectedError
          return
        }
        this.linkAccount = {
          error,
          serviceFrom: fromProviderId,
          serviceToProviderId: oauthMethods[0],
          email,
        }
        this.$refs['link-account-modal'].show()
      } catch (fetchError) {
        // eslint-disable-next-line no-console
        console.error('[login] fetchSignInMethodsForEmail error', fetchError)
        this.message = this.labels.unexpectedError
      }
    },

    async doLinkAccounts() {
      const { linkAccount } = this
      if (!linkAccount) {
        // eslint-disable-next-line no-console
        console.log('[ERROR] link account not found')
        return this.$refs['link-account-modal'].hide()
      }

      const { error, serviceToProviderId } = linkAccount

      const pendingCred = OAuthProvider.credentialFromError(error)

      return signInWithPopup(
        projectAuth,
        this.getProviderForProviderId(serviceToProviderId)
      )
        .then(result =>
          linkWithCredential(result.user, pendingCred)
            .then(async resultLink => {
              const {
                user: {
                  stsTokenManager: { accessToken },
                },
              } = resultLink
              // eslint-disable-next-line no-console
              // console.log('[login] linkWithCredential', resultLink)
              this.$refs['link-account-modal'].hide()
              this.submitInProgress = true
              try {
                const { token } = await loginWithToken(accessToken)
                this.onLoginSuccess(token)
              } catch (error) {
                this.message = error.message || this.labels.unexpectedError
              }
              this.submitInProgress = false
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error('linkWithCredential error', error)
            })
        )
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('signInWithPopup error', error)
        })
        .finally(() => {
          this.linkAccount = null
          return this.$refs['link-account-modal'].hide()
        })
    },

    async microsoftLogin() {
      this.message = null
      projectAuth.tenantId = this.tenantId

      // removing firebase persistence
      await setPersistence(projectAuth, inMemoryPersistence)

      const provider = this.getProviderForProviderId('microsoft.com')
      return signInWithPopup(projectAuth, provider)
        .then(async ({ user: { stsTokenManager: { accessToken }, email } }) => {
          // eslint-disable-next-line no-console
          // console.log('[login] microsoft', accessToken)
          this.submitInProgress = true
          try {
            this.message = `${this.labels.ssoEmail} ${email}`

            const { token } = await loginWithToken(accessToken)
            this.onLoginSuccess(token)
          } catch (error) {
            this.message = error.message || this.labels.unexpectedError
          }
          this.submitInProgress = false
        })
        .catch(error => {
          const errorCode = error.code
          const errorMessage = error.message
          if (errorCode === 'auth/account-exists-with-different-credential') {
            this.handleLinkAccountError(error, 'microsoft.com')
          } else {
            // eslint-disable-next-line no-console
            console.log('[login] microsoft error', errorCode, errorMessage)
            this.message = this.labels.unexpectedError
          }
        })
    },

    async googleLogin() {
      this.message = null
      projectAuth.tenantId = this.tenantId

      // removing firebase persistence
      await setPersistence(projectAuth, inMemoryPersistence)

      const provider = this.getProviderForProviderId('google.com')
      return signInWithPopup(projectAuth, provider)
        .then(async ({ user: { stsTokenManager: { accessToken }, email } }) => {
          // eslint-disable-next-line no-console
          // console.log('[login] google', accessToken)
          this.submitInProgress = true
          try {
            this.message = `${this.labels.ssoEmail} ${email}`

            const { token } = await loginWithToken(accessToken)
            this.onLoginSuccess(token)
          } catch (error) {
            this.message = error.message || this.labels.unexpectedError
          }
          this.submitInProgress = false
        })
        .catch(error => {
          const errorCode = error.code
          const errorMessage = error.message
          if (errorCode === 'auth/account-exists-with-different-credential') {
            this.handleLinkAccountError(error, 'google.com')
          } else {
            // eslint-disable-next-line no-console
            console.log('[login] google error', errorCode, errorMessage)
            this.message = this.labels.unexpectedError
          }
        })
    },

    async customProviderLogin() {
      this.message = null
      projectAuth.tenantId = this.tenantId

      // removing firebase persistence
      await setPersistence(projectAuth, inMemoryPersistence)

      const providerId = this.resolvedProviderId
      const provider = this.getProviderForProviderId(providerId)
      return signInWithPopup(projectAuth, provider)
        .then(async ({ user: { stsTokenManager: { accessToken }, email } }) => {
          this.submitInProgress = true
          try {
            this.message = `${this.labels.ssoEmail} ${email}`

            const { token } = await loginWithToken(accessToken)

            this.onLoginSuccess(token)
          } catch (error) {
            this.message = error.message || this.labels.unexpectedError
          }
          this.submitInProgress = false
        })
        .catch(error => {
          const errorCode = error.code
          const errorMessage = error.message

          if (errorCode === 'auth/account-exists-with-different-credential') {
            this.handleLinkAccountError(error, providerId)
          } else {
            // eslint-disable-next-line no-console
            console.log(`[login] ${providerId} error`, errorCode, errorMessage)
            this.message = this.labels.unexpectedError
          }
        })
    },

    login() {
      if (this.submitInProgress) return false

      const isLoginFormInvalid = this.$refs.form.checkValidity() === false

      if (isLoginFormInvalid) {
        this.$refs.form.reportValidity()
        this.$refs.recaptcha.resetRecaptcha()
        return false
      }

      this.submitInProgress = true
      this.message = null

      // Trigger Turnstile verification — recaptchaVerify will call _doLogin
      this.$refs.recaptcha.execute()
    },

    async _doLogin() {

      try {
        const { token } = await login(this.form)
        this.onLoginSuccess(token)
      } catch (error) {
        const { code } = error
        if (code) {
          const passwordExpired = code === AuthErrorCodes.PasswordExpired

          if (passwordExpired) {
            await this.$router.push({
              name: 'login.password',
              query: { code },
              params: { email: this.form.email },
            })
            return
          }

          const onlySSO = code === AuthErrorCodes.SSORedirect
          if (onlySSO) {
            this.onlySSO = onlySSO
          }
        }

        unconfirmedValidation(this, error)
        this.message = error.message || this.labels.unexpectedError
        this.$refs.recaptcha.resetRecaptcha()
      }

      this.submitInProgress = false
    },

    onLoginSuccess(jwt) {
      saveUserData(jwt)
      this.$store.dispatch('setLanguage', getUser().language)
      this.$root.$emit('login', getUser())
      this.$store.dispatch('user/loadUserInfo')
      this.$ability.update(ability().rules)
      this.redirect()
    },

    async redirect() {
      const redirectUrl = sessionStorage.getItem('redirectUrl')

      if (redirectUrl) {
        const env = window.env || process.env
        const host = env.VUE_APP_NEW_LINK

        const redirectTo = new URL(redirectUrl, host)
        redirectTo.searchParams.append('l', localStorage.languageabrev)

        await generateCookie()

        sessionStorage.removeItem('redirectUrl')
        window.location.assign(redirectTo.toString())
        return
      }

      const { redirectAfterLogin } = localStorage

      if (redirectAfterLogin) {
        delete localStorage.redirectAfterLogin
      }

      const canRedirectToSurvey =
        redirectAfterLogin === '/survey' && this.$can('use', 'engagement')

      const canAccessNewProfile = this.canAccessNewProfile

      const isRedirectNotToDashboardOrSurvey =
        redirectAfterLogin &&
        !['/dashboard', '/survey'].includes(redirectAfterLogin)

      if (isRedirectNotToDashboardOrSurvey || canRedirectToSurvey) {
        this.$router.push(redirectAfterLogin)
      } else if (canAccessNewProfile && isToNewLayout('login-redirect')) {
        await generateCookie()

        const env = window.env || process.env
        const host = env.VUE_APP_NEW_PROFILE_HOST
        window.location = `${host}/home?l=${localStorage.languageabrev}`
      } else {
        this.$router.push('dashboard')
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';

.recaptcha {
  display: flex;
  justify-content: center;
}

.forgot-and-register {
  display: flex;
  justify-content: center;
}

.grecaptcha-badge {
  visibility: hidden;
}

.only-sso-message {
  padding-top: 20px;
}

.line-with-or {
  display: flex;
  align-items: center;
  text-align: center;
  padding: 20px 0;
  font-size: 0.85rem;
}

.line-with-or::before,
.line-with-or::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid map-get($theme-colors, 'gray01');
}

.line-with-or:not(:empty)::before {
  margin-right: 0.5em;
}

.line-with-or:not(:empty)::after {
  margin-left: 0.5em;
}
</style>
